import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';
import { SITE } from '@/lib/site';
import { getSeo } from '@/lib/seo';

const services = [
  {
    title: 'Website design',
    body: 'Websites built by professionals with decades of collective experience. Fast, secure, and built with SEO in mind by default.',
  },
  {
    title: 'Managed hosting',
    body: 'Need us to also host your site? No problem. We will host it afterwards utilising serverless design. Your website will be hosted globally. ',
  },
  {
    title: 'Ongoing care',
    body: "If you go with our hosting package, we'll make minor changes and fixes to your website free of charge for as long as you're with us. Need something more complex? Give us a call.",
  },
];

const faqs = [
  {
    q: 'Where are you based?',
    a: 'We are primarily based in the UK and serve British businesses (hence the name), but we have served clients internationally.',
  },
  {
    q: 'Do I have to host with you?',
    a: "Nope. We understand sometimes you have existing hosting and just need a newly built website. You do not have to host with us.",
  },
  {
    q: 'How long does a site take?',
    a: 'The average website takes 2-4 weeks. More complex websites with lots of moving parts may take significantly longer. Conversely, simple pages can be done in a day or two. Give us an email or call for further information.',
  },
  {
    q: 'What about SEO?',
    a: 'All our websites are built with SEO in mind by default. We can also provide additional SEO services if needed, but most of our clients find the built-in SEO to be more than sufficient.',
  },
];

export default function Landing() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE.name,
    url: SITE.url,
    email: SITE.email,
    telephone: SITE.phone,
    description: SITE.description,
    areaServed: 'GB',
    sameAs: [],
  };

  return (
    <>
      <Seo
        path="/"
        fullTitle={getSeo('home').title}
        description={getSeo('home').description}
        ogImage={getSeo('home').og_image ?? undefined}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 opacity-60"
          style={{
            background:
              'radial-gradient(60rem 40rem at 70% -10%, rgb(var(--accent) / 0.18), transparent 60%), radial-gradient(40rem 30rem at 0% 30%, rgb(var(--accent-deep) / 0.12), transparent 60%)',
          }}
        />
        <div className="mx-auto max-w-page px-5 pt-20 pb-24 sm:px-8 sm:pt-28 sm:pb-32">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-bg-surface px-3 py-1 text-xs text-fg-muted">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            British Built · Booking new projects
          </p>
          <h1 className="font-display text-4xl leading-[1.05] tracking-tight sm:text-6xl md:text-7xl">
            Websites built properly,
            <br className="hidden sm:block" />
            <span className="text-accent">hosted properly</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-fg-muted sm:text-xl">
            We design, build, and host websites for British businesses (and a
            few from further afield). They're designed to be fast, secure, and globally hosted.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to="/contact" className="btn-primary px-5 py-3 text-base">
              Start a project
            </Link>
            <Link to="/about" className="btn-ghost px-5 py-3 text-base">
              How we work
            </Link>
          </div>

          <dl className="mt-14 grid grid-cols-2 gap-x-8 gap-y-6 sm:grid-cols-4">
            <Stat label="Sites from" value="£45" sub="per page" />
            <Stat label="Hosting from" value="£9.99" sub="per month" />
            <Stat label="Average time needed" value="2-4 weeks" sub="Most websites can be done quickly" />
            <Stat label="Support" value="Direct" sub="email a real person" />
          </dl>
        </div>
      </section>

      {/* Services */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-page px-5 py-20 sm:px-8 sm:py-24">
          <div className="max-w-2xl">
            <p className="text-sm font-medium uppercase tracking-wider text-accent">
              What we do
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl">
              The whole website, from request to after-care
            </h2>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {services.map((s) => (
              <article key={s.title} className="card p-6">
                <h3 className="text-xl">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-fg-muted">
                  {s.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-page px-5 py-20 sm:px-8 sm:py-24">
          <div className="grid gap-12 md:grid-cols-2 md:items-center">
            <div>
              <p className="text-sm font-medium uppercase tracking-wider text-accent">
                Pricing
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl">
                Simple, honest, by the page.
              </h2>
              <p className="mt-4 text-fg-muted">
                Most quotes come back to a per-page price plus monthly hosting.
                No retainer minimums, no surprise add-ons, no "platform fees".
              </p>
              <ul className="mt-6 space-y-3 text-sm text-fg-muted">
                <Bullet>Fixed quote before any work begins.</Bullet>
                <Bullet>Hosting bundled with every build.</Bullet>
                <Bullet>British VAT-compliant invoicing.</Bullet>
                <Bullet>Cancel hosting any time after the first 3 months.</Bullet>
              </ul>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <PriceCard
                title="Development"
                price="£45"
                unit="/ page"
                features={[
                  'Custom design, no themes',
                  'Mobile-first, responsive',
                  'On-page SEO + schema',
                  'Accessibility checked',
                ]}
              />
              <PriceCard
                title="Hosting"
                price="£9.99"
                unit="/ month"
                accent
                features={[
                  'UK-routed, fast globally',
                  'SSL + automatic renewals',
                  'Daily backups',
                  'Email forwarding',
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section>
        <div className="mx-auto max-w-page px-5 py-20 sm:px-8 sm:py-24">
          <div className="grid gap-12 md:grid-cols-3">
            <div>
              <p className="text-sm font-medium uppercase tracking-wider text-accent">
                Questions
              </p>
              <h2 className="mt-3 text-3xl sm:text-4xl">Before you ask.</h2>
              <p className="mt-4 text-fg-muted">
                Anything not covered? Ping us at{' '}
                <a className="link" href={`mailto:${SITE.email}`}>
                  {SITE.email}
                </a>
                .
              </p>
            </div>
            <div className="md:col-span-2">
              <dl className="divide-y divide-border border-y border-border">
                {faqs.map((f) => (
                  <div key={f.q} className="py-5">
                    <dt className="text-base font-medium text-fg-base">
                      {f.q}
                    </dt>
                    <dd className="mt-2 text-sm leading-relaxed text-fg-muted">
                      {f.a}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-bg-surface">
        <div className="mx-auto max-w-page px-5 py-16 sm:px-8 sm:py-20">
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl">Got a project in mind?</h2>
              <p className="mt-2 text-fg-muted">
                Tell us what you need and your budget. We'll come back with a
                fixed quote.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary px-5 py-3 text-base">
                Get a quote
              </Link>
              <a href={SITE.phoneHref} className="btn-ghost px-5 py-3 text-base">
                {SITE.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-fg-subtle">
        {label}
      </dt>
      <dd className="mt-1 font-display text-2xl text-fg-base sm:text-3xl">
        {value}
      </dd>
      <dd className="text-xs text-fg-muted">{sub}</dd>
    </div>
  );
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        className="mt-0.5 shrink-0 text-accent"
      >
        <path
          d="M4 12.5l5 5L20 7"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>{children}</span>
    </li>
  );
}

function PriceCard({
  title,
  price,
  unit,
  features,
  accent,
}: {
  title: string;
  price: string;
  unit: string;
  features: string[];
  accent?: boolean;
}) {
  return (
    <div
      className={
        'card p-6 ' +
        (accent ? 'ring-1 ring-accent/40' : '')
      }
    >
      <p className="text-xs uppercase tracking-wider text-fg-subtle">{title}</p>
      <p className="mt-2 font-display text-4xl">
        <span className={accent ? 'text-accent' : ''}>{price}</span>
        <span className="ml-1 text-base font-sans text-fg-muted">{unit}</span>
      </p>
      <ul className="mt-5 space-y-2 text-sm text-fg-muted">
        {features.map((f) => (
          <Bullet key={f}>{f}</Bullet>
        ))}
      </ul>
    </div>
  );
}
